// Privacy.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Privacy.css";

export default function Privacy() {
  return (
    <>
      <Helmet>
        <title>Política de Privacidad | Wayo</title>
        <meta name="description" content="Política de privacidad de Wayo. Cómo protegemos tus datos personales. Cumplimiento RGPD y LOPD." />
        <meta name="keywords" content="privacidad, protección datos, RGPD, LOPD, wayo, política privacidad" />
        <link rel="canonical" href="https://wayoai.app/privacy" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Política de Privacidad | Wayo" />
        <meta property="og:description" content="Cómo protegemos tus datos personales" />
        <meta property="og:url" content="https://wayoai.app/privacy" />
        <meta property="og:type" content="website" />
      </Helmet>
      <main className="privacy-page">
        <Header />
      
      <section className="privacy-content">
        <div className="privacy-container">
          <h1 className="privacy-title">Política de Privacidad</h1>
          
          <div className="privacy-text">
            <p className="last-updated"><strong>Última actualización:</strong> 2 de diciembre de 2025</p>
            
            <p>En Wayo, nos tomamos muy en serio la privacidad y protección de tus datos personales. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos y protegemos tu información cuando utilizas nuestra aplicación.</p>

            <p>Esta Política se aplica a todos los usuarios de Wayo y cumple con el Reglamento General de Protección de Datos (RGPD) de la Unión Europea y la Ley Orgánica de Protección de Datos (LOPD) española.</p>

            <div className="important-box">
              <h3>📌 Puntos Clave</h3>
              <ul>
                <li>Solo recopilamos datos necesarios para proporcionar el servicio</li>
                <li>Tus fotos se usan para analizar alimentos y mejorar nuestra IA</li>
                <li>Nunca vendemos tus datos personales a terceros</li>
                <li>Tienes control total sobre tus datos en todo momento</li>
                <li>Puedes eliminar tu cuenta y datos cuando quieras</li>
              </ul>
            </div>

            <h2>1. Responsable del Tratamiento</h2>
            
            <div className="data-controller-box">
              <p><strong>Identidad:</strong> Facundo Ezequiel Solis</p>
              <p><strong>Dirección:</strong> Calle Albareda, 77, 2D, Las Palmas de Gran Canaria, España</p>
              <p><strong>Email de contacto:</strong> <a href="mailto:soporte@wayoai.app">soporte@wayoai.app</a></p>
              <p><strong>Sitio web:</strong> <a href="https://wayoai.app" target="_blank" rel="noopener noreferrer">wayoai.app</a></p>
            </div>

            <h2>2. Datos Personales que Recopilamos</h2>

            <h3>2.1 Datos de Registro y Cuenta</h3>
            <p>Cuando creas una cuenta en Wayo, recopilamos:</p>
            <ul>
              <li><strong>Nombre</strong> (o nombre de usuario)</li>
              <li><strong>Dirección de correo electrónico</strong></li>
              <li><strong>Contraseña</strong> (almacenada de forma cifrada)</li>
              <li><strong>Fecha de registro</strong></li>
              <li><strong>Preferencias de idioma</strong></li>
            </ul>

            <h3>2.2 Datos de Perfil y Objetivos</h3>
            <p>Para personalizar tu experiencia, puedes proporcionarnos opcionalmente:</p>
            <ul>
              <li><strong>Edad, sexo y datos antropométricos</strong> (peso, altura)</li>
              <li><strong>Objetivos de salud</strong> (pérdida de peso, ganancia muscular, mantenimiento)</li>
              <li><strong>Nivel de actividad física</strong></li>
              <li><strong>Preferencias alimentarias</strong> (vegetariano, vegano, alergias, etc.)</li>
              <li><strong>Objetivos nutricionales</strong> (calorías diarias, macronutrientes)</li>
            </ul>

            <h3>2.3 Fotografías de Alimentos</h3>
            <p>Cuando utilizas la función de escaneo de alimentos:</p>
            <ul>
              <li><strong>Imágenes de comida</strong> que capturas o subes</li>
              <li><strong>Metadatos de las imágenes</strong> (fecha, hora, tipo de archivo)</li>
              <li><strong>Información de ubicación</strong> (solo si autorizas el acceso y está en los metadatos EXIF)</li>
            </ul>
            
            <div className="warning-box">
              <p><strong>⚠️ Importante sobre las fotografías:</strong></p>
              <p>Las fotografías que subes se utilizan para:</p>
              <ul>
                <li>Identificar alimentos mediante inteligencia artificial</li>
                <li>Estimar valores nutricionales</li>
                <li>Mantener tu historial de comidas</li>
                <li><strong>Mejorar y entrenar nuestros modelos de IA</strong> (de forma anónima)</li>
              </ul>
              <p>Eliminamos automáticamente los metadatos EXIF sensibles (ubicación GPS, información del dispositivo) antes de procesar las imágenes.</p>
            </div>

            <h3>2.4 Datos de Actividad y Uso</h3>
            <p>Recopilamos información sobre cómo utilizas la aplicación:</p>
            <ul>
              <li><strong>Registro de comidas</strong> y seguimiento nutricional</li>
              <li><strong>Historial de escaneos</strong> de alimentos</li>
              <li><strong>Interacciones con la aplicación</strong> (funciones utilizadas, tiempo de uso)</li>
              <li><strong>Logros y progreso</strong> hacia tus objetivos</li>
              <li><strong>Preferencias y configuraciones</strong> de la aplicación</li>
            </ul>

            <h3>2.5 Información Técnica</h3>
            <p>Automáticamente recopilamos datos técnicos:</p>
            <ul>
              <li><strong>Tipo de dispositivo</strong> y sistema operativo</li>
              <li><strong>Versión de la aplicación</strong></li>
              <li><strong>Dirección IP</strong></li>
              <li><strong>Identificador único del dispositivo</strong></li>
              <li><strong>Información de red</strong> (operador móvil, tipo de conexión)</li>
              <li><strong>Registros de errores y crashes</strong></li>
            </ul>

            <h3>2.6 Datos de Suscripción y Pagos</h3>
            <p>Cuando realizas una compra:</p>
            <ul>
              <li><strong>Tipo de suscripción</strong> y estado</li>
              <li><strong>Fecha de compra</strong> y renovación</li>
              <li><strong>Identificador de transacción</strong> (de Apple/Google)</li>
            </ul>
            <p><strong>Nota importante:</strong> Los datos de pago (tarjeta de crédito, etc.) son procesados directamente por Apple App Store o Google Play Store. Nosotros NO almacenamos ni tenemos acceso a tu información bancaria o de tarjeta de crédito.</p>

            <h2>3. Base Legal para el Tratamiento de Datos</h2>
            
            <p>Según el RGPD (Art. 6), tratamos tus datos personales basándonos en:</p>

            <h3>3.1 Ejecución de Contrato</h3>
            <p>El tratamiento es necesario para:</p>
            <ul>
              <li>Crear y gestionar tu cuenta</li>
              <li>Proporcionar las funcionalidades de la aplicación</li>
              <li>Procesar tus solicitudes y consultas</li>
            </ul>

            <h3>3.2 Consentimiento</h3>
            <p>Tratamos ciertos datos con tu consentimiento explícito:</p>
            <ul>
              <li><strong>Fotografías para entrenamiento de IA</strong> (puedes revocar este consentimiento)</li>
              <li><strong>Datos de salud opcionales</strong> (peso, altura, objetivos)</li>
              <li><strong>Comunicaciones de marketing</strong> (si las aceptas)</li>
              <li><strong>Acceso a cámara y galería</strong></li>
            </ul>

            <h3>3.3 Interés Legítimo</h3>
            <p>Para mejorar nuestro servicio:</p>
            <ul>
              <li>Análisis de uso y estadísticas anónimas</li>
              <li>Detección y prevención de fraude</li>
              <li>Seguridad de la aplicación</li>
              <li>Mejoras técnicas y corrección de errores</li>
            </ul>

            <h3>3.4 Obligación Legal</h3>
            <p>Cuando debemos cumplir con requisitos legales:</p>
            <ul>
              <li>Cumplimiento de obligaciones fiscales</li>
              <li>Respuesta a requerimientos legales</li>
            </ul>

            <h2>4. Cómo Utilizamos tus Datos</h2>

            <h3>4.1 Proporcionar el Servicio</h3>
            <ul>
              <li>Crear y mantener tu cuenta de usuario</li>
              <li>Procesar fotografías mediante IA para identificar alimentos</li>
              <li>Calcular y presentar información nutricional</li>
              <li>Realizar seguimiento de tu ingesta calórica y nutricional</li>
              <li>Gestionar tus objetivos de salud y fitness</li>
              <li>Sincronizar tus datos entre dispositivos</li>
            </ul>

            <h3>4.2 Mejorar Nuestro Servicio</h3>
            <ul>
              <li><strong>Entrenar y mejorar modelos de IA</strong> para reconocimiento de alimentos (usando fotos de forma anónima)</li>
              <li>Mejorar la precisión de las estimaciones nutricionales</li>
              <li>Desarrollar nuevas funcionalidades</li>
              <li>Analizar patrones de uso para optimizar la experiencia</li>
              <li>Realizar investigación y desarrollo</li>
            </ul>

            <h3>4.3 Comunicación Contigo</h3>
            <ul>
              <li>Enviar notificaciones sobre tu cuenta y actividad</li>
              <li>Responder a tus consultas y solicitudes de soporte</li>
              <li>Informar sobre actualizaciones importantes del servicio</li>
              <li>Enviar recordatorios (si los activas)</li>
              <li>Comunicaciones de marketing (solo si das tu consentimiento)</li>
            </ul>

            <h3>4.4 Seguridad y Prevención de Fraude</h3>
            <ul>
              <li>Proteger la seguridad de la aplicación</li>
              <li>Detectar y prevenir actividades fraudulentas</li>
              <li>Investigar y resolver problemas técnicos</li>
              <li>Hacer cumplir nuestros Términos y Condiciones</li>
            </ul>

            <h2>5. Compartir y Divulgación de Datos</h2>

            <p><strong>Principio fundamental:</strong> NO vendemos tus datos personales a terceros bajo ninguna circunstancia.</p>

            <h3>5.1 Proveedores de Servicios</h3>
            <p>Compartimos datos con proveedores que nos ayudan a operar el servicio:</p>

            <ul>
              <li><strong>Servicios de alojamiento en la nube</strong> (para almacenar datos de forma segura)</li>
              <li><strong>Proveedores de IA</strong> (para procesamiento de imágenes y reconocimiento de alimentos)
                <ul>
                  <li>Ejemplo: OpenAI para análisis de imágenes</li>
                  <li>Los datos se envían de forma segura y cifrada</li>
                  <li>Estos proveedores están sujetos a acuerdos de confidencialidad</li>
                </ul>
              </li>
              <li><strong>Servicios de análisis</strong> (datos anónimos y agregados)</li>
              <li><strong>Servicios de email</strong> (para comunicaciones transaccionales)</li>
              <li><strong>Herramientas de monitorización de errores</strong></li>
            </ul>

            <p>Todos estos proveedores están obligados contractualmente a:</p>
            <ul>
              <li>Proteger tus datos</li>
              <li>Usarlos solo para los fines especificados</li>
              <li>No divulgarlos a terceros</li>
              <li>Cumplir con RGPD si procesan datos de usuarios UE</li>
            </ul>

            <h3>5.2 Apple App Store y Google Play Store</h3>
            <p>Para procesar suscripciones y pagos, compartimos datos mínimos (identificador de usuario) con las plataformas de aplicaciones. Ellos procesan los pagos según sus propias políticas de privacidad.</p>

            <h3>5.3 Requisitos Legales</h3>
            <p>Podemos divulgar tus datos si es necesario para:</p>
            <ul>
              <li>Cumplir con leyes, regulaciones o procesos legales</li>
              <li>Responder a solicitudes gubernamentales válidas</li>
              <li>Proteger nuestros derechos, privacidad, seguridad o propiedad</li>
              <li>Proteger la seguridad de los usuarios o el público</li>
            </ul>

            <h3>5.4 Transferencias de Negocio</h3>
            <p>En caso de fusión, adquisición o venta de activos, tus datos personales pueden ser transferidos. Te notificaremos antes de que tus datos sean transferidos y estén sujetos a una política de privacidad diferente.</p>

            <h2>6. Transferencias Internacionales de Datos</h2>

            <p>Algunos de nuestros proveedores de servicios (como servicios de IA) pueden estar ubicados fuera de la Unión Europea.</p>

            <p>Cuando transferimos datos fuera de la UE/EEE, garantizamos un nivel adecuado de protección mediante:</p>
            <ul>
              <li><strong>Cláusulas Contractuales Tipo</strong> (SCC) aprobadas por la Comisión Europea</li>
              <li><strong>Decisiones de adecuación</strong> de la Comisión Europea</li>
              <li><strong>Certificaciones</strong> como Privacy Shield (si aplica)</li>
              <li><strong>Tu consentimiento explícito</strong> cuando sea necesario</li>
            </ul>

            <p>Ejemplo: Cuando usas OpenAI para análisis de imágenes, tus datos pueden procesarse en servidores en EE.UU. OpenAI está certificado bajo el marco de Privacy Shield y tiene medidas de seguridad equivalentes al RGPD.</p>

            <h2>7. Retención de Datos</h2>

            <p>Conservamos tus datos personales solo durante el tiempo necesario para los fines descritos:</p>

            <h3>7.1 Cuenta Activa</h3>
            <ul>
              <li><strong>Datos de cuenta:</strong> Mientras tu cuenta esté activa</li>
              <li><strong>Fotografías:</strong> Hasta que las elimines o cierres tu cuenta</li>
              <li><strong>Historial de comidas:</strong> Hasta que lo elimines o cierres tu cuenta</li>
            </ul>

            <h3>7.2 Después de Cerrar la Cuenta</h3>
            <ul>
              <li><strong>Eliminación completa:</strong> 30 días después de solicitar el cierre</li>
              <li><strong>Datos para entrenamiento de IA:</strong> Las fotos ya utilizadas para entrenar modelos (de forma anónima) no pueden "des-entrenarse", pero no están vinculadas a tu identidad</li>
              <li><strong>Datos legales/fiscales:</strong> Hasta 6 años si hay obligación legal de conservación</li>
            </ul>

            <h3>7.3 Copias de Seguridad</h3>
            <p>Tus datos pueden permanecer en copias de seguridad hasta 90 días adicionales, pero no estarán accesibles activamente.</p>

            <h2>8. Tus Derechos según el RGPD</h2>

            <p>Como usuario en la Unión Europea, tienes los siguientes derechos sobre tus datos personales:</p>

            <h3>8.1 Derecho de Acceso (Art. 15 RGPD)</h3>
            <p>Puedes solicitar una copia de todos los datos personales que tenemos sobre ti.</p>
            <p><strong>Cómo ejercerlo:</strong> Envía un email a soporte@wayoai.app o usa la función "Descargar mis datos" en la configuración de la app.</p>

            <h3>8.2 Derecho de Rectificación (Art. 16 RGPD)</h3>
            <p>Puedes corregir datos inexactos o incompletos.</p>
            <p><strong>Cómo ejercerlo:</strong> Actualiza tu perfil directamente en la app o contacta a soporte@wayoai.app</p>

            <h3>8.3 Derecho de Supresión / "Derecho al Olvido" (Art. 17 RGPD)</h3>
            <p>Puedes solicitar la eliminación de tus datos personales.</p>
            <p><strong>Cómo ejercerlo:</strong> Usa "Eliminar mi cuenta" en configuración o contacta a soporte@wayoai.app</p>
            <p><strong>Excepciones:</strong> Podemos retener ciertos datos si hay obligación legal (ej: datos fiscales durante 6 años).</p>

            <h3>8.4 Derecho de Portabilidad (Art. 20 RGPD)</h3>
            <p>Puedes recibir tus datos en formato estructurado, de uso común y lectura mecánica (JSON).</p>
            <p><strong>Cómo ejercerlo:</strong> Usa "Exportar datos" en configuración o contacta a soporte@wayoai.app</p>

            <h3>8.5 Derecho de Oposición (Art. 21 RGPD)</h3>
            <p>Puedes oponerte al tratamiento de tus datos en ciertas circunstancias.</p>
            <p><strong>Cómo ejercerlo:</strong> Contacta a soporte@wayoai.app explicando tu oposición.</p>

            <h3>8.6 Derecho a Limitar el Tratamiento (Art. 18 RGPD)</h3>
            <p>Puedes solicitar que limitemos el uso de tus datos mientras se resuelve una disputa.</p>
            <p><strong>Cómo ejercerlo:</strong> Contacta a soporte@wayoai.app</p>

            <h3>8.7 Derecho a Retirar el Consentimiento</h3>
            <p>Puedes retirar tu consentimiento en cualquier momento:</p>
            <ul>
              <li><strong>Uso de fotos para IA:</strong> Desactiva en configuración → Privacidad → "Permitir usar fotos para mejorar IA"</li>
              <li><strong>Comunicaciones de marketing:</strong> Desactiva en configuración o haz clic en "Cancelar suscripción" en los emails</li>
            </ul>

            <h3>8.8 Derecho a Reclamar</h3>
            <p>Tienes derecho a presentar una reclamación ante la autoridad de protección de datos:</p>
            <div className="contact-authority">
              <p><strong>Agencia Española de Protección de Datos (AEPD)</strong></p>
              <p>Web: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a></p>
              <p>Teléfono: 901 100 099 / 912 663 517</p>
              <p>Dirección: C/ Jorge Juan, 6, 28001 Madrid, España</p>
            </div>

            <div className="important-box">
              <p><strong>💡 Respuesta a solicitudes:</strong></p>
              <p>Responderemos a tu solicitud en un plazo máximo de <strong>30 días</strong> desde su recepción. Si necesitamos más tiempo (por la complejidad de la solicitud), te informaremos.</p>
            </div>

            <h2>9. Seguridad de los Datos</h2>

            <p>Implementamos medidas técnicas y organizativas para proteger tus datos:</p>

            <h3>9.1 Medidas Técnicas</h3>
            <ul>
              <li><strong>Cifrado en tránsito:</strong> Todas las comunicaciones usan HTTPS/TLS</li>
              <li><strong>Cifrado en reposo:</strong> Datos sensibles cifrados en la base de datos</li>
              <li><strong>Contraseñas:</strong> Almacenadas con hash bcrypt (no guardamos contraseñas en texto plano)</li>
              <li><strong>Autenticación:</strong> Tokens JWT con expiración</li>
              <li><strong>Firewalls:</strong> Protección de servidores y bases de datos</li>
              <li><strong>Copias de seguridad:</strong> Backups diarios cifrados</li>
              <li><strong>Monitorización:</strong> Detección de accesos no autorizados</li>
            </ul>

            <h3>9.2 Medidas Organizativas</h3>
            <ul>
              <li>Acceso limitado solo a personal autorizado</li>
              <li>Auditorías de seguridad periódicas</li>
              <li>Procedimientos de respuesta ante incidentes</li>
              <li>Acuerdos de confidencialidad con empleados y proveedores</li>
            </ul>

            <h3>9.3 En Caso de Brecha de Seguridad</h3>
            <p>Si ocurre una brecha de seguridad que afecte tus datos:</p>
            <ul>
              <li>Te notificaremos en <strong>72 horas</strong></li>
              <li>Informaremos a la AEPD según lo requiere el RGPD</li>
              <li>Tomaremos medidas inmediatas para mitigar el daño</li>
              <li>Te informaremos de los pasos a seguir para protegerte</li>
            </ul>

            <h2>10. Datos de Menores</h2>

            <p>Wayo está diseñado para usuarios de <strong>14 años o más</strong>.</p>

            <ul>
              <li>Si tienes entre 14 y 17 años, necesitas el consentimiento y supervisión de un padre/tutor</li>
              <li>NO recopilamos intencionalmente datos de menores de 14 años</li>
              <li>Si descubrimos que hemos recopilado datos de un menor de 14 años, los eliminaremos inmediatamente</li>
            </ul>

            <p>Si eres padre/tutor y crees que tu hijo menor de 14 años nos ha proporcionado datos personales, contacta inmediatamente a: soporte@wayoai.app</p>

            <h2>11. Cookies y Tecnologías Similares</h2>

            <h3>11.1 En la Aplicación Móvil</h3>
            <p>La aplicación NO utiliza cookies, pero sí usa:</p>
            <ul>
              <li><strong>Almacenamiento local:</strong> Para guardar preferencias y datos de sesión</li>
              <li><strong>Identificadores únicos:</strong> Para vincular tu dispositivo con tu cuenta</li>
              <li><strong>Tokens de autenticación:</strong> Para mantener tu sesión activa</li>
            </ul>

            <h3>11.2 En el Sitio Web (wayoai.app)</h3>
            <p>Nuestro sitio web utiliza cookies:</p>

            <h4>Cookies Esenciales (Necesarias)</h4>
            <p>Requeridas para que el sitio funcione correctamente:</p>
            <ul>
              <li>Cookies de sesión</li>
              <li>Cookies de seguridad</li>
            </ul>

            <h4>Cookies Analíticas (Opcionales)</h4>
            <p>Para entender cómo usas el sitio (solo con tu consentimiento):</p>
            <ul>
              <li>Google Analytics (anónimo)</li>
              <li>Estadísticas de uso</li>
            </ul>

            <h4>Control de Cookies</h4>
            <p>Puedes:</p>
            <ul>
              <li>Aceptar o rechazar cookies no esenciales en el banner de cookies</li>
              <li>Configurar tu navegador para bloquear cookies</li>
              <li>Eliminar cookies en cualquier momento desde tu navegador</li>
            </ul>

            <h2>12. Marketing y Comunicaciones</h2>

            <h3>12.1 Comunicaciones Transaccionales</h3>
            <p>Enviaremos emails necesarios para el servicio (NO puedes cancelarlos):</p>
            <ul>
              <li>Confirmación de registro</li>
              <li>Restablecimiento de contraseña</li>
              <li>Cambios importantes en el servicio</li>
              <li>Respuestas a tus consultas</li>
            </ul>

            <h3>12.2 Comunicaciones de Marketing</h3>
            <p>SOLO si das tu consentimiento explícito, enviaremos:</p>
            <ul>
              <li>Newsletters con consejos de nutrición</li>
              <li>Información sobre nuevas funcionalidades</li>
              <li>Ofertas especiales y promociones</li>
            </ul>

            <p><strong>Cancelar suscripción:</strong> En cualquier momento:</p>
            <ul>
              <li>Haz clic en "Cancelar suscripción" en cualquier email de marketing</li>
              <li>Desactiva en: Configuración → Notificaciones → Marketing</li>
              <li>Envía un email a: soporte@wayoai.app</li>
            </ul>

            <h2>13. Enlaces a Sitios de Terceros</h2>

            <p>Wayo puede contener enlaces a sitios web o servicios de terceros. Esta Política de Privacidad no aplica a dichos sitios.</p>

            <p>Recomendamos que leas las políticas de privacidad de cualquier sitio de terceros que visites.</p>

            <p>No somos responsables de las prácticas de privacidad de terceros.</p>

            <h2>14. Cambios a esta Política de Privacidad</h2>

            <p>Podemos actualizar esta Política de Privacidad ocasionalmente para reflejar:</p>
            <ul>
              <li>Cambios en nuestras prácticas de datos</li>
              <li>Nuevas funcionalidades del servicio</li>
              <li>Cambios en la legislación</li>
              <li>Mejoras en seguridad o privacidad</li>
            </ul>

            <h3>Notificación de Cambios</h3>
            <p>Te notificaremos sobre cambios materiales mediante:</p>
            <ul>
              <li><strong>Email</strong> a tu dirección registrada</li>
              <li><strong>Notificación en la app</strong></li>
              <li><strong>Aviso en nuestro sitio web</strong></li>
            </ul>

            <p>La versión actualizada entrará en vigor inmediatamente tras su publicación o en la fecha especificada en el aviso.</p>

            <p><strong>Tu uso continuado del servicio después de los cambios constituye tu aceptación de la nueva Política de Privacidad.</strong></p>

            <p>Si no estás de acuerdo con los cambios, debes dejar de usar el servicio y puedes solicitar la eliminación de tu cuenta.</p>

            <h2>15. Jurisdicción y Ley Aplicable</h2>

            <p>Esta Política de Privacidad se rige por:</p>
            <ul>
              <li><strong>Reglamento General de Protección de Datos (RGPD)</strong> de la UE</li>
              <li><strong>Ley Orgánica 3/2018 de Protección de Datos (LOPD)</strong> de España</li>
              <li><strong>Legislación española</strong> aplicable</li>
            </ul>

            <p>Para cualquier disputa relacionada con esta Política, se aplicará la jurisdicción establecida en nuestros Términos y Condiciones.</p>

            <h2>16. Preguntas y Contacto</h2>

            <p>Si tienes preguntas, preocupaciones o solicitudes sobre esta Política de Privacidad o el tratamiento de tus datos personales, puedes contactarnos:</p>

            <div className="contact-box">
              <h3>📧 Contacto de Privacidad</h3>
              <p><strong>Email:</strong> <a href="mailto:soporte@wayoai.app">soporte@wayoai.app</a></p>
              <p><strong>Asunto:</strong> "Privacidad - [Tu consulta]"</p>
              <p><strong>Respuesta:</strong> En un plazo máximo de 30 días</p>
            </div>

            <div className="contact-box">
              <h3>📍 Dirección Postal</h3>
              <p>Facundo Ezequiel Solis</p>
              <p>Calle Albareda, 77, 2D</p>
              <p>Las Palmas de Gran Canaria</p>
              <p>España</p>
            </div>

            <hr />

            <div className="final-notice">
              <h3>✅ Compromiso de Wayo</h3>
              <p>En Wayo nos comprometemos a:</p>
              <ul>
                <li>✓ Proteger tu privacidad en todo momento</li>
                <li>✓ Ser transparentes sobre cómo usamos tus datos</li>
                <li>✓ Darte control total sobre tu información</li>
                <li>✓ Cumplir estrictamente con el RGPD y legislación española</li>
                <li>✓ Responder rápidamente a tus consultas y solicitudes</li>
                <li>✓ Mejorar continuamente nuestras prácticas de privacidad</li>
              </ul>
            </div>

            <div className="acceptance-notice">
              <p><strong>AL UTILIZAR WAYO, CONFIRMAS QUE HAS LEÍDO Y COMPRENDIDO ESTA POLÍTICA DE PRIVACIDAD Y ACEPTAS EL TRATAMIENTO DE TUS DATOS PERSONALES SEGÚN LO DESCRITO.</strong></p>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </main>
    </>
  );
}